import { useState } from 'react'
import { TableContainer, Table, Td, Th, Tr, ResultSection, Hint, BoardPath, SummaryGrid, SummaryItem } from './styles'
import { ResultSummary } from './ResultSummary'
import { PrintStyles, ResultToolbar, Tab, TabList, TabPanel } from './resultStyles'
import { downloadResultCsv } from './resultExport'
import { Card } from '../../../styles/global'
import {
  IDR_REASONS,
  IDR_SCOPE_LABELS,
  formatConnection,
  formatIdr,
  formatNumber,
  formatProtection,
  formatStepRange,
  formatWire,
  formatWireDetails,
  incompleteReason,
  isIncomplete,
  orderBoardsAsTree,
} from './format'

function PhaseSummary({ summary, circuits }) {
  const hasManyPhases = summary.phases.length > 1

  return (
    <>
      <TableContainer>
        <Table>
          <thead>
            <Tr>
              <Th>Fase</Th>
              <Th>Potência (W)</Th>
              <Th>Corrente (A)</Th>
              <Th>Circuitos</Th>
            </Tr>
          </thead>
          <tbody>
            {summary.phases.map((p) => (
              <Tr key={p.phase}>
                <Td>{p.phase}</Td>
                <Td>{p.totalPower}</Td>
                <Td>{p.current}</Td>
                <Td>{p.circuitIndexes.map((i) => circuits[i]?.id).join(', ')}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      </TableContainer>
      <p>Potência total: {summary.totalPower} W</p>
      <p>Maior corrente de fase: {summary.maxPhaseCurrent} A</p>
      {hasManyPhases && <p>Desequilíbrio entre fases: {summary.imbalancePercentage}%</p>}
      {hasManyPhases && Number(summary.imbalancePercentage) > 20 && (
        <Hint>
          Com poucos circuitos monofásicos o desequilíbrio percentual tende a ser alto; avalie-o junto das correntes por
          fase.
        </Hint>
      )}
    </>
  )
}

function BoardResult({ boardResult, path, boardNames, demandRules, isRoot }) {
  const { boardDemand, loadBalancing, mainProtection, feeders } = boardResult
  const circuitLabels = boardResult.circuitRefs.map((ref) => ({
    id: `${boardNames.get(ref.boardId)} · ${ref.circuitIndex + 1}`,
  }))
  const steps = boardDemand.groups.flatMap((group) => group.steps.map((step) => ({ group, step })))

  return (
    <Card>
      <h2>
        {boardResult.name}
        {isRoot ? ' (quadro raiz)' : ''}
      </h2>
      {path.length > 1 && <BoardPath>{path.join(' › ')}</BoardPath>}

      <SummaryGrid>
        <SummaryItem>
          <span>Potência instalada</span>
          <strong>{formatNumber(boardDemand.installedPowerKva)} kVA</strong>
        </SummaryItem>
        <SummaryItem>
          <span>Demanda do quadro</span>
          <strong>{formatNumber(boardDemand.demandPowerKva)} kVA</strong>
        </SummaryItem>
        <SummaryItem>
          <span>Disjuntor geral</span>
          <strong>{formatProtection(mainProtection)}</strong>
          <small>Corrente de projeto: {formatNumber(mainProtection.designCurrent)} A</small>
        </SummaryItem>
        <SummaryItem>
          <span>Cabo de alimentação</span>
          <strong>{formatWire(mainProtection)}</strong>
          {formatWireDetails(mainProtection).map((line) => (
            <small key={line}>{line}</small>
          ))}
          <small>
            Fator de correção: {formatNumber(mainProtection.correctionFactor)} (F<sub>t</sub> ={' '}
            {formatNumber(mainProtection.temperatureFactor)} × F<sub>a</sub> ={' '}
            {formatNumber(mainProtection.groupFactor)}
            {mainProtection.conductor?.neutralCorrectionFactor !== undefined &&
              mainProtection.conductor.neutralCorrectionFactor !== 1 && (
                <>
                  {' '}
                  × F<sub>n</sub> = {formatNumber(mainProtection.conductor.neutralCorrectionFactor)}
                </>
              )}
            )
          </small>
        </SummaryItem>
      </SummaryGrid>
      {isIncomplete(mainProtection) && (
        <Hint>Dimensionamento da alimentação não concluído: {incompleteReason(mainProtection)}</Hint>
      )}

      <ResultSection>
        <h3>Circuitos do quadro</h3>
        {boardResult.circuits.length > 0 ? (
          <TableContainer>
            <Table>
              <thead>
                <Tr>
                  <Th>Número</Th>
                  <Th>Nome</Th>
                  <Th>Tensão (V)</Th>
                  <Th>Potência instalada (W)</Th>
                  <Th>Corrente (A)</Th>
                  <Th>Proteção (A)</Th>
                  <Th>Instalação</Th>
                  <Th>
                    F<sub>a</sub>
                  </Th>
                  <Th>Cabo</Th>
                  <Th>Ligação</Th>
                </Tr>
              </thead>
              <tbody>
                {boardResult.circuits.map((c, index) => (
                  <Tr key={index}>
                    <Td>{c.id}</Td>
                    <Td>{c.name}</Td>
                    <Td>{c.tension}</Td>
                    <Td>{c.totalPower}</Td>
                    <Td>{c.electricCurrent}</Td>
                    <Td>{c.breaker?.current}</Td>
                    <Td>{c.conductor ? `${c.conductor.method} · ${c.conductor.insulation}` : '-'}</Td>
                    <Td>{formatNumber(c.groupFactor)}</Td>
                    <Td>{c.wire?.name}</Td>
                    <Td>{formatConnection(c.phases)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableContainer>
        ) : (
          <Hint>Sem circuitos próprios neste quadro.</Hint>
        )}
      </ResultSection>

      {boardResult.idrs?.length > 0 && (
        <ResultSection>
          <h3>IDRs</h3>
          <TableContainer>
            <Table>
              <thead>
                <Tr>
                  <Th>IDR</Th>
                  <Th>Nome</Th>
                  <Th>Escopo</Th>
                  <Th>Circuitos</Th>
                  <Th>Corrente por fase (A)</Th>
                  <Th>Corrente de projeto (A)</Th>
                  <Th>Corrente requerida (A)</Th>
                  <Th>IDR selecionado</Th>
                </Tr>
              </thead>
              <tbody>
                {boardResult.idrs.map((idr) => (
                  <Tr key={idr.id}>
                    <Td>{idr.id}</Td>
                    <Td>{idr.name}</Td>
                    <Td>{IDR_SCOPE_LABELS[idr.scope] || idr.scope}</Td>
                    <Td>{idr.circuitIndexes.map((i) => boardResult.circuits[i]?.id).join(', ') || '-'}</Td>
                    <Td>
                      {idr.phaseCurrents?.length
                        ? idr.phaseCurrents
                            .map(
                              (p) => `${p.phase}: ${formatNumber(p.current)} (disj. ${formatNumber(p.breakerCurrent)})`
                            )
                            .join(' · ')
                        : '-'}
                    </Td>
                    <Td>{formatNumber(idr.designCurrent)}</Td>
                    <Td>{formatNumber(idr.requiredCurrent)}</Td>
                    <Td>{formatIdr(idr)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableContainer>
          <Hint>
            Corrente calculada com a potência instalada, sem fator de demanda, limitada pelos disjuntores a jusante ou
            pelo disjuntor geral. A seleção considera apenas corrente e polos; sensibilidade, classe e seletividade
            devem ser verificadas pelo projetista.
          </Hint>
          {boardResult.idrs.some((idr) => idr.reason) && (
            <Hint>
              {boardResult.idrs
                .filter((idr) => idr.reason)
                .map((idr) => `${idr.id}: ${IDR_REASONS[idr.reason] || idr.reason}`)
                .join(' ')}
            </Hint>
          )}
        </ResultSection>
      )}

      {steps.length > 0 && (
        <ResultSection>
          <h3>Memória de cálculo da demanda</h3>
          <TableContainer>
            <Table>
              <thead>
                <Tr>
                  <Th>Regra</Th>
                  <Th>Faixa</Th>
                  <Th>Aparelhos</Th>
                  <Th>Potência aplicada (kVA)</Th>
                  <Th>FD</Th>
                  <Th>Demanda (kVA)</Th>
                </Tr>
              </thead>
              <tbody>
                {steps.map(({ group, step }, index) => (
                  <Tr key={index}>
                    <Td>{demandRules.find((rule) => rule.id === group.ruleId)?.name || group.ruleId}</Td>
                    <Td>{formatStepRange(step, group)}</Td>
                    <Td>{step.applianceCount}</Td>
                    <Td>{formatNumber(step.appliedPowerKva)}</Td>
                    <Td>{formatNumber(step.factor)}</Td>
                    <Td>{formatNumber(step.demandPowerKva)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableContainer>
        </ResultSection>
      )}

      {feeders.length > 0 && (
        <ResultSection>
          <h3>Alimentadores de saída</h3>
          <TableContainer>
            <Table>
              <thead>
                <Tr>
                  <Th>Quadro alimentado</Th>
                  <Th>Demanda (kVA)</Th>
                  <Th>Corrente (A)</Th>
                  <Th>Disjuntor no {boardResult.name}</Th>
                  <Th>Cabo</Th>
                </Tr>
              </thead>
              <tbody>
                {feeders.map((feeder) => (
                  <Tr key={feeder.boardId}>
                    <Td>{feeder.name}</Td>
                    <Td>{formatNumber(feeder.boardDemand.demandPowerKva)}</Td>
                    <Td>{formatNumber(feeder.protection.designCurrent)}</Td>
                    <Td>{formatProtection(feeder.protection)}</Td>
                    <Td>{formatWire(feeder.protection)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableContainer>
        </ResultSection>
      )}

      {circuitLabels.length > 0 && (
        <ResultSection>
          <h3>Balanceamento de fases - carga demandada</h3>
          <PhaseSummary summary={loadBalancing} circuits={circuitLabels} />
          {loadBalancing.installedLoad && (
            <ResultSection>
              <h3>Carga instalada nas mesmas ligações</h3>
              <PhaseSummary summary={loadBalancing.installedLoad} circuits={circuitLabels} />
            </ResultSection>
          )}
        </ResultSection>
      )}
    </Card>
  )
}

export function BoardsResult({ result, demandRules }) {
  const boardNames = new Map(result.boards.map((board) => [board.id, board.name]))
  const resultsById = new Map(result.boards.map((board) => [board.id, board]))
  const orderedBoards = orderBoardsAsTree(result.boards)
  const [selectedId, setSelectedId] = useState(result.rootBoardId)
  const activeId = orderedBoards.some(({ board }) => board.id === selectedId) ? selectedId : orderedBoards[0]?.board.id

  return (
    <div data-print-area>
      <PrintStyles />
      <Card>
        <h2>Resultado</h2>
        <ResultSummary result={result} />
        <Hint>
          Os circuitos terminais são dimensionados com a potência instalada, sem fator de demanda. A demanda de cada
          quadro é recalculada com os circuitos próprios e os de todos os quadros alimentados por ele.
        </Hint>
        {!result.optimization.optimal && result.supplyType !== 'single-phase' && (
          <Hint>
            A busca de balanceamento atingiu o limite de cálculo: as ligações do quadro raiz são a melhor distribuição
            encontrada, sem comprovação de ótimo global.
          </Hint>
        )}
        <ResultToolbar data-no-print>
          <button type="button" onClick={() => downloadResultCsv(result)}>
            Exportar CSV
          </button>
          <button type="button" onClick={() => window.print()}>
            Imprimir / PDF
          </button>
        </ResultToolbar>
        {orderedBoards.length > 1 && (
          <TabList aria-label="Quadros">
            {orderedBoards.map(({ board, depth }) => (
              <Tab
                key={board.id}
                id={`tab-${board.id}`}
                aria-selected={board.id === activeId}
                aria-controls={`panel-${board.id}`}
                $active={board.id === activeId}
                $depth={depth}
                onClick={() => setSelectedId(board.id)}
              >
                {board.name}
              </Tab>
            ))}
          </TabList>
        )}
      </Card>
      {orderedBoards.map(({ board, path }) => (
        <TabPanel
          key={board.id}
          id={`panel-${board.id}`}
          aria-labelledby={`tab-${board.id}`}
          $active={board.id === activeId}
        >
          <BoardResult
            boardResult={resultsById.get(board.id)}
            path={path}
            boardNames={boardNames}
            demandRules={demandRules}
            isRoot={board.id === result.rootBoardId}
          />
        </TabPanel>
      ))}
    </div>
  )
}
